import { createSlice } from "@reduxjs/toolkit";
import {
  createSignUp,
  socialSignup,
  verifyOtp,
  resendOtp,
  loginUser,
  forgotPassword,
  resetPassword
} from "../actions/auth";
import { updateProfile, getProfile } from "../actions/profile";

const initialState = {
  loading: false,
  success: false, // general success (signup/social signup/login)
  error: null,
  isOtpVerified: false, // for OTP verification success
  user: null, // user object from backend
  token: null, // auth token if returned
  message: null, // last server message
  status: null, // last server status code
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // optional helpers you can dispatch from components
    clearAuthError(state) {
      state.error = null;
    },
    resetAuthState(state) {
      state.loading = false;
      state.success = false;
      state.error = null;
      state.isOtpVerified = false;
      state.user = null;
      state.token = null;
      state.message = null;
      state.status = null;
    },
    updateUserData(state, action) {
      state.user = { ...state.user, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // =================== SIGNUP ===================
      .addCase(createSignUp.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
        state.message = null;
        state.status = null;
      })
      .addCase(createSignUp.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.status = payload?.status ?? 200;
        state.message = payload?.message ?? null;
        // backend may return user in payload.data or payload.data.user
        // state.user = payload?.data?.user ?? payload?.data ?? null;
        // localStorage.setItem('token', payload?.data?.token)
        // keep success true for 200-level responses; adjust if your backend uses different codes
        state.success = payload?.status === 200 || payload?.status === "success" || !!payload;
        state.error = null;
      })
      .addCase(createSignUp.rejected, (state, { payload }) => {
        state.loading = false;
        state.success = false;
        state.error = payload || "Signup failed";
        state.message = payload || null;
      })

      // =================== SOCIAL SIGNUP ===================
      .addCase(socialSignup.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
        state.message = null;
        state.status = null;
      })
      .addCase(socialSignup.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.status = payload?.status ?? 200;
        state.message = payload?.message ?? null;
        state.user = payload?.data?.user ?? payload?.data ?? state.user;
        state.token = payload?.data?.token ?? state.token;
        state.success = payload?.status === 200;
        state.error = null;
      })
      .addCase(socialSignup.rejected, (state, { payload }) => {
        state.loading = false;
        state.success = false;
        state.error = payload || "Social signup failed";
        state.message = payload || null;
      })

      // =================== VERIFY OTP ===================
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.isOtpVerified = false;
        state.error = null;
        state.message = null;
        state.status = null;
      })
      .addCase(verifyOtp.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.status = payload?.status ?? 200;
        state.message = payload?.message ?? null;
        state.user = payload?.data?.user ?? payload?.data ?? null;
        state.token = payload?.data?.token ?? null;
        if (payload?.data?.token) {
          localStorage.setItem('token', payload?.data?.token);
        }
        state.success = payload?.status === 200 || payload?.status === "success" || !!payload;
        state.error = null;
      })
      .addCase(verifyOtp.rejected, (state, { payload }) => {
        state.loading = false;
        state.isOtpVerified = false;
        state.success = false;
        state.error = payload || "OTP verification failed";
        state.message = payload || null;
      })

      // =================== RESEND OTP ===================
      .addCase(resendOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
        state.status = null;
      })
      .addCase(resendOtp.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.status = payload?.status ?? 200;
        state.message = payload?.message ?? "OTP resent";
        // optionally backend might return user or partial data
        state.user = payload?.data?.user ?? state.user;
        state.success = payload?.status === 200;
        state.error = null;
      })
      .addCase(resendOtp.rejected, (state, { payload }) => {
        state.loading = false;
        state.success = false;
        state.error = payload || "Failed to resend OTP";
        state.message = payload || null;
      })

      // =================== FORGOT PASSWORD (NEW) ===================
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
        state.message = null;
        state.status = null;
        // Optionally clear OTP state if a new flow is starting
        state.isOtpVerified = false;
      })
      .addCase(forgotPassword.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.status = payload?.status ?? 200;
        state.message = payload?.message ?? "Password reset initiated. Check email/phone for code.";
        state.success = payload?.status === 200;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, { payload }) => {
        state.loading = false;
        state.success = false;
        state.error = payload || "Failed to initiate password reset";
        state.message = payload || null;
      })

      // =================== RESET PASSWORD (NEW) ===================
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
        state.message = null;
        state.status = null;
      })
      .addCase(resetPassword.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.status = payload?.status ?? 200;
        state.message = payload?.message ?? "Password successfully reset.";
        state.success = payload?.status === 200;
        state.error = null;
        // Upon successful reset, clear OTP state and potentially login the user if token is returned
        state.isOtpVerified = false;
        state.user = payload?.data?.user ?? state.user;
        state.token = payload?.data?.token ?? state.token;
      })
      .addCase(resetPassword.rejected, (state, { payload }) => {
        state.loading = false;
        state.success = false;
        state.error = payload || "Failed to reset password";
        state.message = payload || null;
      })

      // =================== LOGIN ===================
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
        state.status = null;
      })
      .addCase(loginUser.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.status = payload?.status ?? 200;
        state.message = payload?.message ?? null;
        // Store user data for both OTP verified and non-verified cases
        debugger
        state.user = payload?.data?.user ?? payload?.data ?? state.user;
        state.token = payload?.data?.token ?? payload?.data?.accessToken ?? state.token;
        if (payload?.data?.token) {
          localStorage.setItem('token', payload?.data?.token);
        }
        state.success = payload?.status === 200;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, { payload }) => {
        state.loading = false;
        state.success = false;
        state.error = payload || "Login failed";
        state.message = payload || null;
      })

      // =================== UPDATE PROFILE ===================
      .addCase(updateProfile.fulfilled, (state, { payload }) => {
        // Update user data when profile is updated
        if (state.user && payload?.data) {
          state.user = { ...state.user, ...payload.data };
        }
      })

      // =================== GET PROFILE ===================
      .addCase(getProfile.fulfilled, (state, { payload }) => {
        // Update user data when profile is fetched
        if (payload?.data) {
          state.user = { ...state.user, ...payload.data };
        }
      });
  },
});

export const { clearAuthError, resetAuthState, updateUserData } = authSlice.actions;
export default authSlice.reducer;
